import styled, { type DefaultTheme } from 'styled-components';
import { lighten } from 'polished';
import { COLORS, LAYOUT } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { StyledGenericModalContent } from '@/components/uielements/genericModal/styles';
import { isDark } from '@/utils/global';
import { StyledStepHeader } from '@/components/checkout/uielements';
import { Spinner } from '@/components/uielements/spinner';

const errorColor = (theme: DefaultTheme) => theme.global.error_color || COLORS.ERROR_RED;

export const StyledWalletStepHeader = styled(StyledStepHeader)`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: left;
`;

export const StyledSpinner = styled(Spinner)`
  position: relative;
`;

export const StyledWalletContainer = styled.div({
  padding: `${LAYOUT.GUTTER}px`,

  p: {
    margin: `${LAYOUT.GUTTER / 4}px 0`,
    fontSize: '16px',
  },
});

export const SavedCardRow = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${LAYOUT.GUTTER / 2}px 0`,
  borderBottom: `1px solid ${lighten(0.2, theme.forms.border_color ?? '#a7a7a7')}`,

  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const CardInfo = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const CardDetails = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

export const CardDescription = styled.span({
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: '19.5px',
});

export const CardExpiredLabel = styled.span(({ theme }) => ({
  display: 'inline-block',
  background: lighten(0.46, errorColor(theme)),
  color: errorColor(theme),
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: '15px',
  padding: '2px 4px',
  borderRadius: '4px',
  marginLeft: '8px',
}));

export const CardExpiry = styled.span<{ $expired?: boolean }>(({ $expired, theme }) => ({
  color: $expired ? errorColor(theme) : getFontStyles(theme.checkout.secondary_font).color,
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '16.5px',
}));

export const StyledRemoveButton = styled(StyledPrimaryButton)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '16.5px',
  color: errorColor(theme),
  background: 'none',
  padding: '6px 8px',
  borderColor: errorColor(theme),
  height: '33px',

  '&:hover': {
    background: errorColor(theme),
    color: isDark(errorColor(theme)) ? COLORS.WHITE : theme.text.primary_font.color ?? '',
    borderColor: errorColor(theme),
  },
}));

export const StyledRemoveCardModalContent = styled(StyledGenericModalContent)(() => ({
  padding: `0 ${LAYOUT.GUTTER}px`,
  textAlign: 'left',
}));

export const StyledRemoveCardModalDescription = styled.p(() => ({
  margin: 0,
  fontSize: '14px',
  lineHeight: '21px',
  padding: '16px 0',
}));

export const StyledKeepCardButton = styled(StyledPrimaryButton)(({ theme }) => ({
  width: '100%',
  background: 'transparent',
  color: theme.buttons.primary_button_background_color,
  borderColor: theme.buttons.primary_button_background_color,

  '&:hover': {
    background: theme.buttons.primary_button_background_color,
    color: isDark(theme.buttons.primary_button_background_color)
      ? COLORS.WHITE
      : theme.text.primary_font.color ?? '',
  },

  '&:disabled': {
    opacity: 0.7,
    border: `solid 1px ${theme.buttons.primary_button_background_color}`,
  },
}));

export const StyledRemoveCardConfirmButton = styled(StyledPrimaryButton)(({ theme }) => ({
  width: '100%',
  background: errorColor(theme),
  borderColor: errorColor(theme),
  color: isDark(errorColor(theme)) ? COLORS.WHITE : theme.text.primary_font.color ?? '',

  '&:hover': {
    background: errorColor(theme),
  },

  '&:disabled': {
    opacity: 0.7,
  },
}));
